import { useEffect, useState, useCallback } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';

const testimonials = [
  {
    name: "Sarah Mitchell",
    role: "Founder, Mitchell Designs",
    text: "Trademark Legal Forum made the entire trademark registration process seamless. Their team guided me through every step, and I had my trademark secured in record time. Highly recommend their services!",
    rating: 5,
    image: null
  },
  {
    name: "David Krause",
    role: "CEO, Krause Technologies",
    text: "After receiving an office action from the USPTO, I thought my application was done for. The team at Trademark Legal Forum crafted a compelling response and got my trademark approved. Outstanding service!",
    rating: 5,
    image: null
  },
  {
    name: "Jennifer Alvarez",
    role: "Owner, Alvarez Boutique",
    text: "I needed to register my brand on Amazon and protect it from counterfeits. Trademark Legal Forum handled the trademark registration and Brand Registry enrollment flawlessly. My brand is now fully protected.",
    rating: 5,
    image: null
  },
  {
    name: "Robert Chang",
    role: "Managing Partner, Chang & Associates",
    text: "We used Trademark Legal Forum for our company formation and trademark services. Their team was professional, responsive, and delivered everything on time. They truly understand business needs.",
    rating: 5,
    image: null
  },
  {
    name: "Amanda Foster",
    role: "Creative Director, Foster Media",
    text: "Copyright registration was something I had been putting off for years. Trademark Legal Forum made it simple and straightforward. They protected all my creative works efficiently.",
    rating: 5,
    image: null
  }
];

export default function TestimonialsSlider() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [drag, setDrag] = useState({ isDragging: false, startX: 0, currentX: 0 });

  const totalSlides = testimonials.length;
  const maxIndex = totalSlides - 3; // Show 3 slides at a time

  const slideNext = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, maxIndex]);

  const slidePrev = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, maxIndex]);

  const goToSlide = useCallback((index) => {
    if (isAnimating || index > maxIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, maxIndex]);

  // Drag handlers for testimonials slider
  const handleMouseDown = useCallback((e) => {
    setDrag({ isDragging: true, startX: e.clientX, currentX: 0 });
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!drag.isDragging) return;
    const diff = e.clientX - drag.startX;
    setDrag(prev => ({ ...prev, currentX: diff }));
  }, [drag.isDragging, drag.startX]);

  const handleMouseUp = useCallback(() => {
    if (!drag.isDragging) return;
    const threshold = 50;
    if (drag.currentX > threshold) {
      slidePrev();
    } else if (drag.currentX < -threshold) {
      slideNext();
    }
    setDrag({ isDragging: false, startX: 0, currentX: 0 });
  }, [drag.isDragging, drag.currentX, slidePrev, slideNext]);

  const handleMouseLeave = useCallback(() => {
    if (drag.isDragging) {
      setDrag({ isDragging: false, startX: 0, currentX: 0 });
    }
  }, [drag.isDragging]);

  // Auto-play functionality
  useEffect(() => {
    const interval = setInterval(() => {
      slideNext();
    }, 5000);
    return () => clearInterval(interval);
  }, [slideNext]);

  return (
    <section data-testid="testimonials-section" className="py-20 md:py-32 bg-white">
      <div className="container mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">Testimonials</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 font-['Outfit'] tracking-tight">
            What Our Clients Say
          </h2>
        </div>

        <div className="relative">
            <style>{`
              .testimonial-slider {
                --translate-percent: -34%;
              }
              @media (max-width: 639px) {
                .testimonial-slider {
                  --translate-percent: -108%;
                }
              }
              @media (min-width: 640px) and (max-width: 1023px) {
                .testimonial-slider {
                  --translate-percent: -50%;
                }
              }
            `}</style>
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out gap-2 testimonial-slider cursor-grab active:cursor-grabbing"
                style={{ 
                  '--slide-index': currentIndex,
                  transform: `translateX(calc(var(--slide-index) * var(--translate-percent)))`
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
              >
                {testimonials.map((t, i) => (
                  <div key={i} className="w-full md:w-[calc(33.333%-0.5rem)] flex-shrink-0">
                    <div data-testid={`testimonial-card-${i}`} className="bg-white border border-gray-200 rounded-sm p-8 h-full flex flex-col">
                      <div className="flex gap-1 mb-4">
                        {Array.from({ length: t.rating }).map((_, j) => (
                          <Star key={j} size={14} className="fill-[#D4AF37] text-[#D4AF37]" />
                        ))}
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed flex-1 italic">"{t.text}"</p>
                      <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-200">
                        {t.image ? (
                          <img src={t.image} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-bold text-sm font-['Outfit']">
                            {t.name.split(' ').map(n => n[0]).join('')}
                          </div>
                        )}
                        <div>
                          <p className="text-gray-900 font-semibold text-sm font-['Outfit']">{t.name}</p>
                          <p className="text-gray-600 text-xs">{t.role}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          {/* Nav arrows */}
          <button
            data-testid="testimonials-prev"
            onClick={slidePrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 bg-white border border-gray-200 rounded-full flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors hidden md:flex"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            data-testid="testimonials-next"
            onClick={slideNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 bg-white border border-gray-200 rounded-full flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors hidden md:flex"
          >
            <ChevronRight size={18} />
          </button>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  currentIndex === i ? 'bg-[#D4AF37] w-6' : 'bg-[#1D2D50]'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
